import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid,
  IconButton, InputLabel, MenuItem, Paper, Select, Snackbar, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, useMediaQuery, useTheme
} from '@mui/material';
import { Add, Delete, Edit, FilterAltOff as ClearIcon, PlaylistAdd, Refresh } from '@mui/icons-material';
import { Checkbox, Switch, FormControlLabel } from '@mui/material';
import { academicAPI, gradingAPI, reportCardAPI } from '../../services/api';
import ModulePageHeader from '../common/ModulePageHeader';
import FormDialog from '../common/FormDialog';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';


const EMPTY_FORM = {
  template_code: '',
  comment_type: '',
  comment_text: '',
  grade_boundary_id: null,
  conduct_category_id: null,
  conduct_rating_id: null,
  applicable_grade_min: '',
  applicable_grade_max: '',
  curriculum_level: '',
  percentage_min: '',
  percentage_max: '',
  attendance: 'any',
  conduct: 'any',
  variant: 1,
  priority: 0,
};

const TYPE_COLORS = {
  Subject: 'primary',
  General: 'default',
  'Class Teacher': 'success',
  Headteacher: 'warning'
};

const CONDUCT_TYPES = new Set(['General']);
const PERFORMANCE_TYPES = new Set(['Subject', 'Class Teacher']);
// Headteacher uses both — handled separately

const GRADE_CONTEXT = {
  'Subject':       { label: 'Subject Grade',             hint: 'Grade a student earns in this subject' },
  'Class Teacher': { label: 'Overall Performance Grade', hint: 'Based on average across all subjects' },
  'Headteacher':   { label: 'Overall Performance Grade', hint: 'Based on overall performance AND conduct — select both' },
  'General':       { label: 'Conduct Rating',            hint: 'Based on student conduct rating average' },
};

export default function CommentTemplates() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [templates, setTemplates] = useState([]);
  const [scales, setScales] = useState([]);
  const [boundaries, setBoundaries] = useState([]);
  const [conductCategories, setConductCategories] = useState([]);
  const [conductRatings, setConductRatings] = useState([]);
  const [selectedScale, setSelectedScale] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkShared, setBulkShared] = useState({ comment_type: '' });
  const [bulkRows, setBulkRows] = useState([{ template_code: '', comment_text: '', grade_boundary_id: null, conduct_category_id: null, conduct_rating_id: null }]);
  const [bulkScale, setBulkScale] = useState(null);
  const [bulkBoundaries, setBulkBoundaries] = useState([]);
  const [bulkConductCategory, setBulkConductCategory] = useState(null);
  const [bulkCodeErrors, setBulkCodeErrors] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filterType, setFilterType] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [generateDialog, setGenerateDialog] = useState(false);
  const [generateForm, setGenerateForm] = useState({ academic_year: new Date().getFullYear(), term_id: '', class_id: '', subject: true, class_teacher: true, headteacher: true, preserve_manual: true });
  const [generateResult, setGenerateResult] = useState(null);
  const [terms, setTerms] = useState([]);
  const [classes, setClasses] = useState([]);
  const [levels, setLevels] = useState([]);
  const curriculumLevelOptions = [...new Set(levels.map(l => l.curriculum_level).filter(Boolean))];
  const commentTypeOptions = [...new Set(templates.map(t => t.comment_type).filter(Boolean))];
  const attendanceOptions = [...new Set(['any', ...templates.map(t => t.attendance).filter(v => v && v !== 'any')])];
  const conductOptions = [...new Set(['any', ...templates.map(t => t.conduct).filter(v => v && v !== 'any')])];
  const COMMENT_TYPES = commentTypeOptions.length ? commentTypeOptions : ['Subject', 'General', 'Class Teacher', 'Headteacher'];

  // Load grading scales + conduct data once
  useEffect(() => {
    gradingAPI.getActiveGradingScales()
      .then((res) => setScales(Array.isArray(res.data) ? res.data : res.data?.data ?? []))
      .catch(() => {});
    reportCardAPI.getConductCategories()
      .then((res) => setConductCategories(Array.isArray(res.data) ? res.data : res.data?.data ?? []))
      .catch(() => {});
    reportCardAPI.getConductRatings()
      .then((res) => setConductRatings(Array.isArray(res.data) ? res.data : res.data?.data ?? []))
      .catch(() => {});
    academicAPI.getTerms().then((res) => setTerms(Array.isArray(res.data) ? res.data : res.data?.data ?? [])).catch(() => {});
    academicAPI.getClasses().then((res) => setClasses(Array.isArray(res.data) ? res.data : res.data?.data ?? [])).catch(() => {});
    academicAPI.getLevels().then((res) => setLevels(Array.isArray(res.data) ? res.data : res.data?.data ?? [])).catch(() => {});
  }, []);

  // Load boundaries when scale changes in dialog
  useEffect(() => {
    if (!selectedScale) { setBoundaries([]); return; }
    gradingAPI.getBoundariesForScale(selectedScale.id)
      .then((res) => setBoundaries(Array.isArray(res.data) ? res.data : res.data?.data ?? []))
      .catch(() => setBoundaries([]));
  }, [selectedScale]);

  // Load boundaries for bulk dialog scale
  useEffect(() => {
    if (!bulkScale) { setBulkBoundaries([]); return; }
    gradingAPI.getBoundariesForScale(bulkScale.id)
      .then((res) => setBulkBoundaries(Array.isArray(res.data) ? res.data : res.data?.data ?? []))
      .catch(() => setBulkBoundaries([]));
  }, [bulkScale]);

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterType ? { comment_type: filterType } : {};
      const res = await reportCardAPI.getCommentTemplates(params);
      setTemplates(Array.isArray(res.data) ? res.data : res.data?.data ?? []);
      setSelected(new Set());
    } catch {
      showSnackbar('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => s.size === templates.length ? new Set() : new Set(templates.map((t) => t.id)));

  const handleBulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} selected template(s)?`)) return;
    try {
      await reportCardAPI.bulkDeleteCommentTemplates([...selected]);
      showSnackbar(`${selected.size} templates deleted`);
      await load();
    } catch (err) {
      showSnackbar(err?.response?.data?.message || 'Bulk delete failed', 'error');
    }
  };

  useEffect(() => { load(); }, [load]);

  const closeBulkDialog = () => {
    setBulkDialog(false);
    setBulkShared({ comment_type: '' });
    setBulkRows([{ template_code: '', comment_text: '', grade_boundary_id: null, conduct_category_id: null, conduct_rating_id: null }]);
    setBulkScale(null);
    setBulkBoundaries([]);
    setBulkConductCategory(null);
    setBulkCodeErrors({});
  };

  const handleBulkSave = async () => {
    if (!bulkShared.comment_type) { showSnackbar('Comment type is required', 'error'); return; }
    const validRows = bulkRows.filter((r) => r.template_code.trim() && r.comment_text.trim());
    if (!validRows.length) { showSnackbar('Add at least one row with code and comment text', 'error'); return; }
    if (!validateBulkCodes()) { showSnackbar('Fix duplicate or empty codes before saving', 'error'); return; }

    const criteriaErrors = [];
    validRows.forEach((row) => {
      if (CONDUCT_TYPES.has(bulkShared.comment_type)) {
        if (!row.conduct_category_id || !row.conduct_rating_id) {
          criteriaErrors.push('General templates require conduct category and rating.');
        }
      }
      if (PERFORMANCE_TYPES.has(bulkShared.comment_type)) {
        if (!row.grade_boundary_id) {
          criteriaErrors.push('Subject/Class Teacher templates require a grade.');
        }
      }
      if (bulkShared.comment_type === 'Headteacher') {
        if (!row.grade_boundary_id && !row.conduct_rating_id) {
          criteriaErrors.push('Headteacher templates require a grade, a conduct rating, or both.');
        }
      }
    });
    if (criteriaErrors.length) {
      showSnackbar([...new Set(criteriaErrors)].join(' '), 'error');
      return;
    }

    try {
      await reportCardAPI.bulkCreateCommentTemplates({ comment_type: bulkShared.comment_type, templates: validRows });
      showSnackbar(`${validRows.length} templates created`);
      closeBulkDialog();
      await load();
    } catch (err) {
      const errs = err?.response?.data?.errors;
      showSnackbar(errs ? Object.values(errs).flat().join(', ') : (err?.response?.data?.message || 'Bulk save failed'), 'error');
    }
  };

  const updateBulkRow = (i, key, val) => {
    setBulkRows((rows) => rows.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
    if (key === 'template_code') {
      setBulkCodeErrors((errs) => {
        const next = { ...errs };
        delete next[i];
        return next;
      });
    }
  };

  // Auto-generate rows from grade boundaries
  const handleBulkScaleChange = (scale) => {
    setBulkScale(scale);
    if (!scale) return;
    gradingAPI.getBoundariesForScale(scale.id).then((res) => {
      const bounds = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      setBulkBoundaries(bounds);
      setBulkRows(bounds.map((b) => ({
        template_code: '',
        comment_text: '',
        grade_boundary_id: b.id,
        conduct_category_id: bulkConductCategory?.id ?? null,
        conduct_rating_id: null,
      })));
      setBulkCodeErrors({});
    }).catch(() => setBulkBoundaries([]));
  };

  // Auto-generate rows from conduct ratings when category selected
  const handleBulkConductCategoryChange = (cat) => {
    setBulkConductCategory(cat);
    if (!cat) return;
    setBulkRows(conductRatings.map((r) => ({
      template_code: '',
      comment_text: '',
      grade_boundary_id: null,
      conduct_category_id: cat.id,
      conduct_rating_id: r.id,
    })));
    setBulkCodeErrors({});
  };

  const validateBulkCodes = () => {
    const errs = {};
    const seen = new Set();
    bulkRows.forEach((row, i) => {
      const code = row.template_code.trim();
      if (!code) { errs[i] = 'Required'; return; }
      if (seen.has(code)) { errs[i] = 'Duplicate code in this batch'; return; }
      seen.add(code);
    });
    setBulkCodeErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setSelectedScale(null); setBoundaries([]); setDialog(true); };
  const handleGenerate = async () => {
    if (!generateForm.academic_year || !generateForm.term_id) {
      showSnackbar('Academic year and term are required', 'error'); return;
    }
    try {
      const res = await reportCardAPI.generateComments({
        academic_year: Number(generateForm.academic_year),
        term_id: Number(generateForm.term_id),
        class_id: generateForm.class_id ? Number(generateForm.class_id) : undefined,
        subject: generateForm.subject,
        class_teacher: generateForm.class_teacher,
        headteacher: generateForm.headteacher,
        preserve_manual: generateForm.preserve_manual,
      });
      setGenerateResult(res.data?.data ?? null);
    } catch (err) {
      showSnackbar(err?.response?.data?.message || 'Generation failed', 'error');
    }
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      ...t,
      grade_boundary_id: t.grade_boundary_id ?? null,
      conduct_category_id: t.conduct_category_id ?? null,
      conduct_rating_id: t.conduct_rating_id ?? null,
      curriculum_level: t.curriculum_level ?? '',
      percentage_min: t.percentage_min ?? '',
      percentage_max: t.percentage_max ?? '',
      attendance: t.attendance ?? 'any',
      conduct: t.conduct ?? 'any',
      variant: t.variant ?? 1,
      priority: t.priority ?? 0,
    });
    const scale = t.grade_boundary?.grading_scale ?? null;
    setSelectedScale(scale);
    if (scale) {
      gradingAPI.getBoundariesForScale(scale.id)
        .then((res) => setBoundaries(Array.isArray(res.data) ? res.data : res.data?.data ?? []))
        .catch(() => setBoundaries([]));
    } else {
      setBoundaries([]);
    }
    setDialog(true);
  };
  const closeDialog = () => { setDialog(false); setEditing(null); setForm(EMPTY_FORM); setSelectedScale(null); setBoundaries([]); };

  const handleSave = async () => {
    if (!form.template_code || !form.comment_type || !form.comment_text) {
      showSnackbar('Code, type, and comment text are required', 'error');
      return;
    }
    if (isConductType && (!form.conduct_category_id || !form.conduct_rating_id)) {
      showSnackbar('General templates require conduct category and rating', 'error');
      return;
    }
    if (isPerformanceType && !form.grade_boundary_id) {
      showSnackbar('Subject/Class Teacher templates require a grade', 'error');
      return;
    }
    if (isHeadteacher && !form.grade_boundary_id && !form.conduct_rating_id) {
      showSnackbar('Headteacher templates require a grade, a conduct rating, or both', 'error');
      return;
    }
    try {
      const payload = {
        template_code: form.template_code,
        comment_type: form.comment_type,
        comment_text: form.comment_text,
        grade_boundary_id: (PERFORMANCE_TYPES.has(form.comment_type) || form.comment_type === 'Headteacher')
          ? (form.grade_boundary_id ?? null) : null,
        conduct_category_id: (CONDUCT_TYPES.has(form.comment_type) || form.comment_type === 'Headteacher')
          ? (form.conduct_category_id ?? null) : null,
        conduct_rating_id: (CONDUCT_TYPES.has(form.comment_type) || form.comment_type === 'Headteacher')
          ? (form.conduct_rating_id ?? null) : null,
        curriculum_level: form.curriculum_level || null,
        percentage_min: form.percentage_min !== '' ? Number(form.percentage_min) : null,
        percentage_max: form.percentage_max !== '' ? Number(form.percentage_max) : null,
        attendance: form.attendance || 'any',
        conduct: form.conduct || 'any',
        variant: form.variant !== '' ? Number(form.variant) : 1,
        priority: form.priority !== '' ? Number(form.priority) : 0,
      };
      if (editing) {
        await reportCardAPI.updateCommentTemplate(editing.id, payload);
        showSnackbar('Template updated successfully');
      } else {
        await reportCardAPI.createCommentTemplate(payload);
        showSnackbar('Template created successfully');
      }
      closeDialog();
      await load();
    } catch (err) {
      const errs = err?.response?.data?.errors;
      showSnackbar(errs ? Object.values(errs).flat().join(', ') : (err?.response?.data?.message || 'Failed to save template'), 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await reportCardAPI.deleteCommentTemplate(deleteDialog.id);
      showSnackbar('Template deleted');
      setDeleteDialog({ open: false, id: null });
      await load();
    } catch {
      showSnackbar('Failed to delete template', 'error');
    }
  };

  const field = (key) => ({
    value: form[key] ?? '',
    onChange: (e) => setForm({ ...form, [key]: e.target.value })
  });

  const selectedBoundary = boundaries.find((b) => b.id === form.grade_boundary_id) ?? null;
  const selectedConductCategory = conductCategories.find((c) => c.id === form.conduct_category_id) ?? null;
  const selectedConductRating = conductRatings.find((r) => r.id === form.conduct_rating_id) ?? null;
  const isConductType = CONDUCT_TYPES.has(form.comment_type);
  const isPerformanceType = PERFORMANCE_TYPES.has(form.comment_type);
  const isHeadteacher = form.comment_type === 'Headteacher';

  return (
    <Box>
      <ModulePageHeader
        title="Comment Templates"
        subtitle="Predefined templates for consistent student performance comments"
        actions={[
          <Button key="refresh" variant="outlined" startIcon={<Refresh />} onClick={load} disabled={loading} fullWidth={isMobile}>
            Refresh
          </Button>,
          <Button key="bulk" variant="outlined" startIcon={<PlaylistAdd />} onClick={() => setBulkDialog(true)} fullWidth={isMobile}>
            Bulk Add
          </Button>,
          <Button key="generate" variant="outlined" onClick={() => { setGenerateResult(null); setGenerateDialog(true); }} fullWidth={isMobile}>
            Generate Comments
          </Button>,
          <Button key="add" variant="contained" startIcon={<Add />} onClick={openCreate} fullWidth={isMobile}>
            Add Template
          </Button>
        ]}
      />

      <Card elevation={3}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
            <Typography variant="h6">Comment Templates</Typography>
            <Chip label={templates.length} size="small" color="primary" />
          </Box>

          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 220 }}
              options={COMMENT_TYPES}
              value={filterType || null}
              onChange={(_, v) => setFilterType(v || '')}
              renderInput={(params) => <TextField {...params} label="Filter by Type" />}
            />
            {filterType && (
              <Button size="small" variant="outlined" startIcon={<ClearIcon />} onClick={() => setFilterType('')}>
                Clear
              </Button>
            )}
          </Box>

          {selected.size > 0 && (
            <Stack direction="row" alignItems="center" spacing={1} mb={2} sx={{ p: 1, bgcolor: 'action.selected', borderRadius: 1 }}>
              <Typography variant="body2">{selected.size} selected</Typography>
              <Button size="small" color="error" startIcon={<Delete />} onClick={handleBulkDelete}>Delete Selected</Button>
              <Button size="small" onClick={() => setSelected(new Set())}>Clear</Button>
            </Stack>
          )}

          {loading ? (
            <Box py={6} textAlign="center"><CircularProgress /></Box>
          ) : templates.length === 0 ? (
            <EmptyRecordsState message="No comment templates found." />
          ) : isMobile ? (
            <ResponsiveRecordList
              items={templates}
              renderItem={(t) => (
                <Stack spacing={0.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight={700}>{t.template_code}</Typography>
                    <Chip label={t.comment_type} color={TYPE_COLORS[t.comment_type] || 'default'} size="small" />
                  </Stack>
                  <Typography variant="body2">{t.comment_text}</Typography>
                  {(t.conduct_category || t.conduct_rating) && (
                    <Typography variant="caption" color="text.secondary">
                      Conduct: <strong>{t.conduct_category?.category_name ?? '—'}</strong>
                      {t.conduct_rating ? ` · ${t.conduct_rating.rating_name} (${t.conduct_rating.rating_value})` : ''}
                    </Typography>
                  )}
                  {t.grade_boundary && (
                    <Typography variant="caption" color="text.secondary">
                      Grade: <strong>{t.grade_boundary.grade}</strong>
                      {' '}({t.grade_boundary.min_percentage}–{t.grade_boundary.max_percentage}%)
                      {' · '}{t.grade_boundary.grading_scale?.scale_name}
                    </Typography>
                  )}
                  <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    <IconButton size="small" color="primary" onClick={() => openEdit(t)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, id: t.id })}><Delete fontSize="small" /></IconButton>
                  </Stack>
                </Stack>
              )}
            />
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ bgcolor: 'grey.100' }}>
                      <Checkbox
                        size="small"
                        checked={templates.length > 0 && selected.size === templates.length}
                        indeterminate={selected.size > 0 && selected.size < templates.length}
                        onChange={toggleAll}
                      />
                    </TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Code</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Comment Text</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Grade / Conduct</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.template_code}</TableCell>
                      <TableCell><Chip label={t.comment_type} color={TYPE_COLORS[t.comment_type] || 'default'} size="small" /></TableCell>
                      <TableCell sx={{ maxWidth: 400 }}>{t.comment_text}</TableCell>
                      <TableCell>
                        {[t.conduct_category && `${t.conduct_category.category_name} · ${t.conduct_rating?.rating_name ?? '—'} (${t.conduct_rating?.rating_value ?? ''})`,
                          t.grade_boundary && `${t.grade_boundary.grade} (${t.grade_boundary.min_percentage}–${t.grade_boundary.max_percentage}%) · ${t.grade_boundary.grading_scale?.scale_name ?? ''}`
                        ].filter(Boolean).join(' | ') || '—'}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="primary" onClick={() => openEdit(t)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, id: t.id })}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={bulkDialog} onClose={closeBulkDialog} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>Bulk Add Comment Templates</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Comment Type</InputLabel>
                <Select value={bulkShared.comment_type} label="Comment Type" onChange={(e) => {
                  const type = e.target.value;
                  setBulkShared({ comment_type: type });
                  setBulkScale(null); setBulkBoundaries([]);
                  setBulkConductCategory(null);
                  if (CONDUCT_TYPES.has(type) && conductCategories.length > 0 && conductRatings.length > 0) {
                    const rows = [];
                    conductCategories.forEach((cat) =>
                      conductRatings.forEach((rating) =>
                        rows.push({ template_code: '', comment_text: '', grade_boundary_id: null, conduct_category_id: cat.id, conduct_rating_id: rating.id })
                      )
                    );
                    setBulkRows(rows);
                  } else {
                    setBulkRows([{ template_code: '', comment_text: '', grade_boundary_id: null, conduct_category_id: null, conduct_rating_id: null }]);
                  }
                  setBulkCodeErrors({});
                }}>
                  {COMMENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {bulkShared.comment_type && (
              <>
                {(PERFORMANCE_TYPES.has(bulkShared.comment_type) || bulkShared.comment_type === 'Headteacher') && (
                  <Grid item xs={12} sm={6}>
                    <Autocomplete fullWidth size="small"
                      options={scales}
                      value={bulkScale}
                      onChange={(_, s) => handleBulkScaleChange(s)}
                      getOptionLabel={(s) => `${s.scale_name} (${s.scale_code})`}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => <TextField {...params} label="Grading Scale" helperText="Auto-generates one row per grade" />}
                    />
                  </Grid>
                )}
                {bulkShared.comment_type === 'Headteacher' && (
                  <Grid item xs={12} sm={6}>
                    <Autocomplete fullWidth size="small"
                      options={conductCategories}
                      value={bulkConductCategory}
                      onChange={(_, v) => handleBulkConductCategoryChange(v)}
                      getOptionLabel={(c) => `${c.category_name} (${c.category_code})`}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => <TextField {...params} label="Conduct Category" helperText="Auto-generates one row per rating" />}
                    />
                  </Grid>
                )}
              </>
            )}

            <Grid item xs={12}>
              <Stack spacing={2} sx={{ width: '100%' }}>
                {bulkRows.map((row, i) => (
                  <Box key={i} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, width: '100%' }}>
                    <Grid container spacing={1} alignItems="flex-start">
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth size="small" label="Code"
                          value={row.template_code}
                          error={!!bulkCodeErrors[i]}
                          helperText={bulkCodeErrors[i] ?? (row.grade_boundary_id ? bulkBoundaries.find((b) => b.id === row.grade_boundary_id)?.grade : row.conduct_rating_id ? conductRatings.find((r) => r.id === row.conduct_rating_id)?.rating_name : '')}
                          onChange={(e) => updateBulkRow(i, 'template_code', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth size="small" label="Comment Text" multiline maxRows={3}
                          value={row.comment_text}
                          onChange={(e) => updateBulkRow(i, 'comment_text', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <IconButton size="small" color="error" disabled={bulkRows.length === 1}
                          onClick={() => setBulkRows((rows) => rows.filter((_, idx) => idx !== i))}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Grid>
                      {(CONDUCT_TYPES.has(bulkShared.comment_type) || bulkShared.comment_type === 'Headteacher') && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Autocomplete size="small"
                              options={conductCategories}
                              value={conductCategories.find((c) => c.id === row.conduct_category_id) ?? null}
                              onChange={(_, v) => updateBulkRow(i, 'conduct_category_id', v?.id ?? null)}
                              getOptionLabel={(c) => c.category_name}
                              isOptionEqualToValue={(a, b) => a.id === b.id}
                              renderInput={(params) => <TextField {...params} label="Conduct Category" />}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Autocomplete size="small"
                              options={conductRatings}
                              value={conductRatings.find((r) => r.id === row.conduct_rating_id) ?? null}
                              onChange={(_, v) => updateBulkRow(i, 'conduct_rating_id', v?.id ?? null)}
                              getOptionLabel={(r) => `${r.rating_name} (${r.rating_value})`}
                              isOptionEqualToValue={(a, b) => a.id === b.id}
                              renderInput={(params) => <TextField {...params} label="Conduct Rating" />}
                            />
                          </Grid>
                        </>
                      )}
                      {(PERFORMANCE_TYPES.has(bulkShared.comment_type) || bulkShared.comment_type === 'Headteacher') && (
                        <Grid item xs={12} sm={6}>
                          <Autocomplete size="small"
                            options={bulkBoundaries}
                            value={bulkBoundaries.find((b) => b.id === row.grade_boundary_id) ?? null}
                            disabled={!bulkScale}
                            onChange={(_, b) => updateBulkRow(i, 'grade_boundary_id', b?.id ?? null)}
                            getOptionLabel={(b) => `${b.grade} (${b.min_percentage}–${b.max_percentage}%)`}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderInput={(params) => <TextField {...params} label={bulkScale ? 'Grade' : 'Select scale first'} />}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                ))}
                <Button size="small" startIcon={<Add />}
                  onClick={() => setBulkRows((rows) => [...rows, { template_code: '', comment_text: '', grade_boundary_id: null, conduct_category_id: null, conduct_rating_id: null }])}>
                  Add Row
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkDialog}>Cancel</Button>
          <Button onClick={handleBulkSave} variant="contained">Save All</Button>
        </DialogActions>
      </Dialog>

      {/* Generate Comments Dialog */}
      <Dialog open={generateDialog} onClose={() => setGenerateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Comments</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Academic Year *"
                value={generateForm.academic_year}
                onChange={(e) => setGenerateForm(p => ({ ...p, academic_year: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Term *</InputLabel>
                <Select value={generateForm.term_id} label="Term *"
                  onChange={(e) => setGenerateForm(p => ({ ...p, term_id: e.target.value }))}>
                  {terms.map(t => <MenuItem key={t.id} value={t.id}>{t.name} {t.year}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Class (optional)</InputLabel>
                <Select value={generateForm.class_id} label="Class (optional)"
                  onChange={(e) => setGenerateForm(p => ({ ...p, class_id: e.target.value }))}>
                  <MenuItem value="">All Classes</MenuItem>
                  {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name || c.class_name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {[['subject', 'Subject Comments'], ['class_teacher', 'Class Teacher Comments'], ['headteacher', 'Headteacher Comments'], ['preserve_manual', 'Preserve Manual Overrides']].map(([key, label]) => (
              <Grid item xs={6} key={key}>
                <FormControlLabel
                  control={<Switch checked={generateForm[key]} onChange={(e) => setGenerateForm(p => ({ ...p, [key]: e.target.checked }))} />}
                  label={label}
                />
              </Grid>
            ))}
            {generateResult && (
              <Grid item xs={12}>
                <Alert severity="success">
                  Processed: {generateResult.processed} · Subject: {generateResult.subject_generated} · Class Teacher: {generateResult.class_teacher_generated} · Headteacher: {generateResult.headteacher_generated} · Skipped: {generateResult.skipped}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialog(false)}>Close</Button>
          <Button variant="contained" onClick={handleGenerate}>Generate</Button>
        </DialogActions>
      </Dialog>

      <FormDialog
        open={dialog}
        onClose={closeDialog}
        title={editing ? 'Edit Template' : 'Add Comment Template'}
        onSubmit={handleSave}
        isMobile={isMobile}
      >
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Template Code" required {...field('template_code')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Comment Type</InputLabel>
              <Select value={form.comment_type} label="Comment Type" onChange={(e) => {
                setForm({ ...form, comment_type: e.target.value, grade_boundary_id: null, conduct_category_id: null, conduct_rating_id: null });
                setSelectedScale(null);
                setBoundaries([]);
              }}>
                {COMMENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Comment Text" required multiline rows={4} {...field('comment_text')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Curriculum Level</InputLabel>
              <Select value={form.curriculum_level} label="Curriculum Level"
                onChange={(e) => setForm({ ...form, curriculum_level: e.target.value })}>
                <MenuItem value="">Any</MenuItem>
                {curriculumLevelOptions.map(l => <MenuItem key={l} value={l}>{l.replace(/_/g, ' ')}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size="small" type="number" label="% Min" {...field('percentage_min')} inputProps={{ min: 0, max: 100 }} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size="small" type="number" label="% Max" {...field('percentage_max')} inputProps={{ min: 0, max: 100 }} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size="small" type="number" label="Variant" {...field('variant')} inputProps={{ min: 1, max: 50 }} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size="small" type="number" label="Priority" {...field('priority')} inputProps={{ min: 0, max: 255 }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Attendance</InputLabel>
              <Select value={form.attendance} label="Attendance"
                onChange={(e) => setForm({ ...form, attendance: e.target.value })}>
                {attendanceOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Conduct Filter</InputLabel>
              <Select value={form.conduct} label="Conduct Filter"
                onChange={(e) => setForm({ ...form, conduct: e.target.value })}>
                {conductOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          {form.comment_type && (
            <>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  {GRADE_CONTEXT[form.comment_type]?.hint}
                </Typography>
              </Grid>
              {(isConductType || isHeadteacher) && (
                <>
                  {isHeadteacher && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Conduct</Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      fullWidth size="small"
                      options={conductCategories}
                      value={selectedConductCategory}
                      onChange={(_, v) => setForm({ ...form, conduct_category_id: v?.id ?? null })}
                      getOptionLabel={(c) => `${c.category_name} (${c.category_code})`}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => <TextField {...params} label="Conduct Category" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      fullWidth size="small"
                      options={conductRatings}
                      value={selectedConductRating}
                      onChange={(_, v) => setForm({ ...form, conduct_rating_id: v?.id ?? null })}
                      getOptionLabel={(r) => `${r.rating_name} (${r.rating_value})`}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => <TextField {...params} label="Conduct Rating" />}
                    />
                  </Grid>
                </>
              )}
              {(isPerformanceType || isHeadteacher) && (
                <>
                  {isHeadteacher && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Performance</Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      fullWidth size="small"
                      options={scales}
                      value={selectedScale}
                      onChange={(_, scale) => {
                        setSelectedScale(scale);
                        setForm({ ...form, grade_boundary_id: null });
                      }}
                      getOptionLabel={(s) => `${s.scale_name} (${s.scale_code})`}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => <TextField {...params} label="Grading Scale" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      fullWidth size="small"
                      options={boundaries}
                      value={selectedBoundary}
                      disabled={!selectedScale}
                      onChange={(_, b) => setForm({ ...form, grade_boundary_id: b?.id ?? null })}
                      getOptionLabel={(b) => `${b.grade} (${b.min_percentage}–${b.max_percentage}%)`}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => (
                        <TextField {...params} label={selectedScale ? GRADE_CONTEXT[form.comment_type]?.label : 'Select a scale first'} />
                      )}
                    />
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </FormDialog>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this template? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
